// apps/roleforge-app/src/components/chat/UnsentMessageNotice.tsx
//
// Issue #1326 (1) — pins a message that never reached the server so the owner
// can see it, copy it, and send it again. The full text is rendered (not
// truncated) because in a RoleForge interview the answer is frequently several
// paragraphs and the whole point of this card is that the words still exist.

import type { UnsentMessage } from '../../lib/unsent-message';

interface Props {
  unsent: UnsentMessage;
  /** True while a retry is in flight — disables both actions. */
  busy?: boolean;
  onRetry: () => void;
  onDiscard: () => void;
  className?: string;
}

export function UnsentMessageNotice({
  unsent,
  busy = false,
  onRetry,
  onDiscard,
  className = '',
}: Props) {
  return (
    <div
      data-testid="unsent-message-notice"
      role="status"
      aria-live="polite"
      className={`rounded-xl border border-amber-400/70 bg-card p-4 shadow-soft ${className}`}
    >
      {/* Review M3 — the durable copy lives in sessionStorage, which is
          per-tab and dies with the tab. "Safe" without qualification was a
          promise this storage cannot keep, and the owner would only discover
          that at the moment it mattered. */}
      <p className="text-base font-semibold text-ink">Not sent — still here in this tab</p>
      <p className="mt-1 text-sm text-graphite">{unsent.reason}</p>
      <p className="mt-1 text-xs text-graphite">
        Copy it somewhere safe if you need to close this tab before sending.
      </p>
      <p
        data-testid="unsent-message-text"
        className="mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink"
      >
        {unsent.text}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onRetry}
          data-testid="unsent-message-retry"
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-paper transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send again'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDiscard}
          data-testid="unsent-message-discard"
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink transition hover:bg-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral disabled:opacity-50"
        >
          Discard
        </button>
      </div>
    </div>
  );
}
